import { useState, useEffect } from 'react';
import VendorSimulatorForm from './VendorSimulatorForm';
import VendorConfigFormModal from './VendorConfigFormModal';
import ResultCard from '@/features/super-admin/referral-simulator/components/ResultCard';
import DistributionChart from '@/features/super-admin/referral-simulator/components/DistributionChart';
import ModelsComparison from '@/features/super-admin/referral-simulator/components/ModelsComparison';
import AlertModal from '@/features/super-admin/referral-simulator/components/AlertModal';
import createClient from 'openapi-fetch';
import '@/features/vendor/referral-simulator/referral-simulator.css';
import { vendorApi } from '@/features/vendor/api/vendorApi';
import { localizedFetch } from '@/lib/apiClient';
import { getPersianErrorMessage } from '@/lib/persianUi';
import { formatJalaliDate } from '@/lib/format';

const client = createClient({
    baseUrl: '/api/v1',
    credentials: 'include',
    fetch: localizedFetch,
});

const fmt = (n) => new Intl.NumberFormat('fa-IR').format(Number(n) || 0);

const translateBackendError = (message) => {
    return getPersianErrorMessage({ message }, 'خطای نامشخص.');
};

export default function ReferralSimulator() {
    const [result, setResult] = useState(null);
    const [lastPayload, setLastPayload] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [compareLoading, setCompareLoading] = useState(false);
    const [showComparison, setShowComparison] = useState(false);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertTitle, setAlertTitle] = useState('');

    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedItems, setSelectedItems] = useState({});
    const [fetchingProducts, setFetchingProducts] = useState(false);
    const [discountMetrics, setDiscountMetrics] = useState({
        originalTotal: 0,
        maxDiscountPercent: 0,
        discountAmount: 0,
        finalTotal: 0
    });

    const showAlert = (message, title = 'خطا در اعتبارسنجی') => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertOpen(true);
    };

    useEffect(() => {
        let originalTotal = 0;
        let repTotal = 0;
        let costTotal = 0;

        Object.entries(selectedItems).forEach(([id, qty]) => {
            const p = products.find(prod => prod.id === id);
            if (p) {
                originalTotal += Number(p.base_price) * qty;
                const rep = p.representative_price ? Number(p.representative_price) : Number(p.base_price) * 0.8;
                const cost = p.cost_price ? Number(p.cost_price) : Number(p.base_price) * 0.7;
                repTotal += rep * qty;
                costTotal += cost * qty;
            }
        });

        const HIDDEN_MIN_MARGIN = 0.10;

        // === [CHANGED] محاسبه حداکثر تخفیف بر اساس قیمت پایه (originalTotal) و بهای تمام شده ===
        let maxDiscountPct = 0;
        if (originalTotal > 0) {
            const maxD = 1 - (costTotal / (originalTotal * (1 - HIDDEN_MIN_MARGIN)));
            maxDiscountPct = Math.max(0, Math.min(20, maxD * 100));
        }
        maxDiscountPct = Math.round(maxDiscountPct * 10) / 10;
        // =========================================================================================

        const discountAmount = (originalTotal * maxDiscountPct) / 100;
        const finalTotal = originalTotal - discountAmount;

        setDiscountMetrics({
            originalTotal,
            maxDiscountPercent: maxDiscountPct,
            discountAmount,
            finalTotal
        });
    }, [selectedItems, products]);

    const openItemsModal = async () => {
        setIsModalOpen(true);
        if (products.length > 0) return;

        setFetchingProducts(true);
        try {
            const data = await vendorApi.getSimulatorProducts();
            const eligibleProducts = data.filter(p => p.is_simulator_eligible);
            setProducts(eligibleProducts);
        } catch (err) {
            showAlert('خطا در دریافت لیست محصولات. لطفاً کنسول (F12) را بررسی کنید.', 'خطا');
        } finally {
            setFetchingProducts(false);
        }
    };

    const handleQtyChange = (productId, delta) => {
        setSelectedItems(prev => {
            const current = prev[productId] || 0;
            const next = current + delta;
            if (next <= 0) {
                const copy = { ...prev };
                delete copy[productId];
                return copy;
            }
            return { ...prev, [productId]: next };
        });
    };

    const applySelection = () => setIsModalOpen(false);

    const handleSubmit = async (payload) => {
        setLoading(true);
        setSuccessMsg('');
        try {
            const { data, error } = await client.POST('/vendor/referral/simulate', { body: payload });
            if (error) throw new Error(error.detail || 'خطا');
            setResult(data);
            setLastPayload(payload);
            setShowComparison(false);
        } catch (e) {
            showAlert(translateBackendError(e.message), 'خطا در شبیه‌سازی');
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCompare = async (payload) => {
        setCompareLoading(true);
        try {
            const { distribution, custom_weights, ...rest } = payload;
            const { data, error } = await client.POST('/vendor/referral/compare', {
                body: { ...rest, max_depth: rest.max_depth }
            });
            if (error) throw new Error(error.detail || 'خطا');
            setComparison(data);
            setShowComparison(true);
        } catch (e) {
            showAlert(translateBackendError(e.message), 'خطا در مقایسه مدل‌ها');
        } finally {
            setCompareLoading(false);
        }
    };

    const handleSaveConfig = async (modalPayload) => {
        setSavingConfig(true);
        try {
            const { data, error } = await client.POST('/vendor/referral/configs', {
                body: modalPayload
            });
            if (error) throw new Error(error.detail || 'خطا در ذخیره تنظیمات');

            setIsConfigModalOpen(false);
            setSuccessMsg('برنامه پورسانت اختصاصی فروشگاه شما با موفقیت ذخیره و فعال شد.');
        } catch (e) {
            throw new Error(translateBackendError(e.message));
        } finally {
            setSavingConfig(false);
        }
    };

    const getInitialConfigForModal = () => {
        if (!result || !lastPayload) return null;

        const levelPercents = {};
        if (result.levels) {
            result.levels.forEach(lvl => {
                levelPercents[String(lvl.level)] = lvl.percent;
            });
        }

        return {
            name: `برنامه ${formatJalaliDate(new Date())}`,
            description: 'ایجاد شده از طریق شبیه‌ساز پورسانت',
            buyer_discount_percent: 0,
            level_percents: levelPercents,
            max_depth: lastPayload.max_depth || 3,
            max_total_percent: lastPayload.max_sales_percent || 5,
            min_margin_percent: lastPayload.min_margin_percent || 3,
            requires_active: false,
            active_days_window: 30,
            min_purchase_count: 1,
            is_active: true
        };
    };

    return (
        <div className="referral-simulator">
            <header className="page-header">
                <h1>شبیه‌ساز پورسانت فروشگاه</h1>
                <p>قبل از فعال‌سازی پورسانت‌ها، اثر آن‌ها روی حاشیه سود فروشگاه خود را بررسی کنید.</p>
            </header>

            <section className="form-section">
                <VendorSimulatorForm
                    onSubmit={handleSubmit}
                    onCompare={handleCompare}
                    loading={loading || compareLoading}
                    onValidationError={showAlert}
                    onOpenItemsModal={openItemsModal}
                    externalSalesAmount={discountMetrics.finalTotal > 0 ? discountMetrics.finalTotal : undefined}
                />
            </section>

            {successMsg && (
                <div className="success-banner" style={{ background: '#dcfce7', color: '#15803d', padding: '1rem', borderRadius: '8px', margin: '1rem 0', textAlign: 'center' }}>
                    {successMsg}
                </div>
            )}

            <section className="result-section">
                {!showComparison && (
                    <>
                        <ResultCard result={result} />
                        {result && (
                            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                                <button
                                    onClick={() => setIsConfigModalOpen(true)}
                                    style={{ background: '#10b981', color: '#fff', border: 'none', padding: '0.75rem 2rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)' }}
                                >
                                    ذخیره این تنظیمات برای فروشگاه من
                                </button>
                            </div>
                        )}
                        {result && <DistributionChart levels={result.levels} />}
                    </>
                )}
                {showComparison && <ModelsComparison data={comparison} />}
            </section>

            <AlertModal open={alertOpen} title={alertTitle} message={alertMessage} onClose={() => setAlertOpen(false)} />

            {isConfigModalOpen && (
                <VendorConfigFormModal
                    mode="create"
                    config={getInitialConfigForModal()}
                    onClose={() => setIsConfigModalOpen(false)}
                    onSubmit={handleSaveConfig}
                    loading={savingConfig}
                />
            )}

            {isModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, direction: 'rtl' }}>
                    <div style={{ background: '#fff', width: '90%', maxWidth: '550px', borderRadius: '12px', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>

                        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>انتخاب اقلام خرید گروهی</h3>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#ef4444' }}>✖</button>
                        </div>

                        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
                            {fetchingProducts ? (
                                <div style={{ textAlign: 'center', color: '#6b7280' }}>در حال دریافت اقلام...</div>
                            ) : products.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>
                                    <p>هیچ کالایی برای خرید گروهی تعریف نکرده‌اید.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {products.map(p => {
                                        const qty = selectedItems[p.id] || 0;
                                        return (
                                            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '8px', background: qty > 0 ? '#eff6ff' : '#fff' }}>
                                                <div>
                                                    <strong style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem' }}>{p.name_fa}</strong>
                                                    <span style={{ fontSize: '0.8rem', color: '#059669' }}>{fmt(p.base_price)} ریال</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                                                    <button type="button" onClick={() => handleQtyChange(p.id, 1)} style={{ width: '28px', height: '28px', background: '#f3f4f6', border: 'none', cursor: 'pointer' }}>+</button>
                                                    <span style={{ width: '35px', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{qty}</span>
                                                    <button type="button" onClick={() => handleQtyChange(p.id, -1)} style={{ width: '28px', height: '28px', background: '#f3f4f6', border: 'none', cursor: 'pointer' }}>−</button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div style={{ padding: '1.5rem', borderTop: '1px solid #e5e7eb', background: '#f9fafb', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#374151' }}>
                                <span>مجموع قیمت پایه (قبل از تخفیف):</span>
                                <span>{fmt(discountMetrics.originalTotal)} ریال</span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#059669' }}>
                                <span>حداقل تخفیف خرید گروهی:</span>
                                <strong>0%</strong>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#e11d48' }}>
                                <span>حداکثر تخفیف خرید گروهی:</span>
                                <strong>{discountMetrics.maxDiscountPercent}% (معادل {fmt(discountMetrics.discountAmount)} ریال)</strong>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #d1d5db', fontSize: '1.1rem', color: '#2563eb' }}>
                                <span>مبلغ نهایی پرداختی مشتری:</span>
                                <strong>{fmt(discountMetrics.finalTotal)} ریال</strong>
                            </div>

                            <button onClick={applySelection} disabled={discountMetrics.finalTotal === 0} style={{ width: '100%', padding: '0.75rem', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: discountMetrics.finalTotal === 0 ? 'not-allowed' : 'pointer', opacity: discountMetrics.finalTotal === 0 ? 0.5 : 1 }}>
                                تایید و جایگذاری مبلغ نهایی
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}